import { Icon, ScrollArea, Tooltip, token } from "@may-i/mash-design-system";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import type { Branch } from "~/entities/branch/types";
import type { BranchVersion } from "~/entities/branch-version/types";
import { useBranches } from "~/features/branch/hooks/use-branches";
import { useBranchVersions } from "~/features/branch-version/hooks/use-branch-versions";
import { useSelectedVersion } from "~/features/branch-version/hooks/use-selected-version";
import { useCurrentUser } from "~/features/auth/hooks/use-current-user";
import { formatValidDate } from "~/shared/lib/format";

type FocusPanel = "branch" | "version";


export function BranchSelector() {
  const { t } = useTranslation("shared");
  const navigate = useNavigate();
  const location = useLocation();
  const { branchUid } = useParams();
  const { data: branches } = useBranches();
  const { data: currentUser } = useCurrentUser();
  const isAdmin = currentUser?.isStaff ?? false;
  const { selectedVersion, setSelectedVersion } = useSelectedVersion();
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredBranchUid, setHoveredBranchUid] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const [focusedPanel, setFocusedPanel] = useState<FocusPanel>("branch");
  const [focusedIndex, setFocusedIndex] = useState(0);
  const versionsRef = useRef<BranchVersion[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);

  // 키보드 포커스 이동 시 해당 아이템이 보이도록 자동 스크롤
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const focused = el.querySelector("[data-focused='true']");
    if (focused) {
      focused.scrollIntoView({ block: "nearest" });
    }
  }, [focusedPanel, focusedIndex]);

  // 드롭다운 열릴 때 선택된 브랜치로 스크롤
  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      const el = contentRef.current;
      if (!el) return;
      const selected = el.querySelector("[data-branch-selected='true']");
      if (selected) {
        selected.scrollIntoView({ block: "start" });
      }
    });
  }, [isOpen]);

  const isHome = !branchUid;
  const currentBranch = branches?.find((b) => b.uid === branchUid);
  const hasValue = Boolean(currentBranch);
  const { data: currentBranchVersions } = useBranchVersions(branchUid);
  const isLatestVersion =
    currentBranchVersions && currentBranchVersions.length > 0 && selectedVersion?.id === currentBranchVersions[0].id;

  const firstBranchUid = branches?.[0]?.uid;
  const activeBranchUid = hoveredBranchUid ?? branchUid ?? firstBranchUid;

  const filteredBranches = useMemo(() => {
    if (!branches) return [];
    const query = searchQuery.trim().toLowerCase();
    return query
      ? branches.filter(
          (b) =>
            b.name.toLowerCase().includes(query) ||
            b.client.name.toLowerCase().includes(query),
        )
      : branches;
  }, [branches, searchQuery]);

  // 현재 URL에서 서브경로(resources, camera-mapping 등)를 추출
  const currentSubPath = branchUid
    ? location.pathname.split(`/branch/${branchUid}/`)[1]?.split("/")[0] || "resources"
    : "resources";

  const handleBranchSelect = useCallback(
    (uid: string) => {
      // branch 가 바뀌면 이전 branch 의 version 컨텍스트를 즉시 비워서,
      // WorkflowLayout 의 sync effect 가 새 branch 의 version 으로 채우기 전까지
      // stale version 으로 query 가 발사되지 않도록 한다
      if (uid !== branchUid) {
        setSelectedVersion(undefined);
      }
      navigate(`/branch/${uid}/${currentSubPath}`);
      setIsOpen(false);
      setSearchQuery("");
    },
    [navigate, currentSubPath, setSelectedVersion, branchUid],
  );

  const handleVersionSelect = useCallback(
    (version: BranchVersion) => {
      setSelectedVersion(version);
      navigate(`/branch/${version.branchUid}/${currentSubPath}`);
      setIsOpen(false);
      setSearchQuery("");
    },
    [navigate, setSelectedVersion, currentSubPath],
  );

  const handleManageBranches = () => {
    navigate("/");
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleOpenChange = (open: boolean) => {
    if (isHome) return;
    setIsOpen(open);
    if (open) {
      setHoveredBranchUid(branchUid);
      const idx = filteredBranches.findIndex((b) => b.uid === branchUid);
      setFocusedIndex(idx >= 0 ? idx : 0);
    } else {
      setSearchQuery("");
      setHoveredBranchUid(undefined);
      setFocusedPanel("branch");
      setFocusedIndex(0);
    }
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const maxIndex =
          focusedPanel === "branch" ? filteredBranches.length - 1 : versionsRef.current.length - 1;
        if (maxIndex < 0) return;
        setFocusedIndex((prev) => {
          const next = e.key === "ArrowDown" ? prev + 1 : prev - 1;
          const clamped = next < 0 ? maxIndex : next > maxIndex ? 0 : next;
          if (focusedPanel === "branch") {
            setHoveredBranchUid(filteredBranches[clamped]?.uid);
          }
          return clamped;
        });
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (focusedPanel === "branch") {
          setFocusedPanel("version");
          setFocusedIndex(0);
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (focusedPanel === "version") {
          setFocusedPanel("branch");
          const idx = filteredBranches.findIndex((b) => b.uid === activeBranchUid);
          setFocusedIndex(idx >= 0 ? idx : 0);
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (focusedPanel === "branch" && filteredBranches[focusedIndex]) {
          handleBranchSelect(filteredBranches[focusedIndex].uid);
        } else if (focusedPanel === "version" && versionsRef.current[focusedIndex]) {
          handleVersionSelect(versionsRef.current[focusedIndex]);
        }
      }
    },
    [focusedPanel, focusedIndex, filteredBranches, activeBranchUid, handleBranchSelect, handleVersionSelect],
  );

  // Branch 관리 페이지(홈)에서는 셀렉터 자체를 숨김 — 어차피 선택할 수 없는 상태
  if (isHome) return null;

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger asChild disabled={isHome}>
        <TriggerButton type="button" $hasValue={hasValue} $disabled={isHome}>
          {currentBranch ? (
            <>
              <TriggerBranchName>{currentBranch.name}</TriggerBranchName>
              {selectedVersion && (
                <>
                  <TriggerDivider />
                  <TriggerVersionName>ver. {selectedVersion.name}</TriggerVersionName>
                  {isLatestVersion && (
                    <TriggerLatestBadge>{t("gnb.branchSelector.latestBadge")}</TriggerLatestBadge>
                  )}
                </>
              )}
            </>
          ) : (
            <TriggerPlaceholder>{t("gnb.branchSelector.placeholder")}</TriggerPlaceholder>
          )}
          <TriggerChevron icon={isOpen ? "ic_chevron_up" : "ic_chevron_down"} width={20} />
        </TriggerButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <StyledContent ref={contentRef} sideOffset={4} align="start" collisionPadding={8} onKeyDown={handleKeyDown}>
          <ManageBranchesRow onClick={handleManageBranches}>
            <ManageBranchesLabel>{t("gnb.branchSelector.manageBranches")}</ManageBranchesLabel>
            <Icon icon="ic_setting_fill" width={16} />
          </ManageBranchesRow>
          <SearchBarWrapper>
            <SearchInput>
              <SearchIcon icon="ic_search" width={20} />
              <SearchInputField
                placeholder={t("gnb.branchSelector.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchInput>
          </SearchBarWrapper>
          <CascaderBody>
            <BranchListWrapper>
              <ScrollArea>
                <ListPadding>
                  {filteredBranches.map((branch, index) => (
                    <BranchListItem
                      key={branch.uid}
                      branch={branch}
                      isAdmin={isAdmin}
                      isSelected={branch.uid === branchUid}
                      isHovered={branch.uid === hoveredBranchUid}
                      isFocused={focusedPanel === "branch" && focusedIndex === index}
                      onMouseEnter={() => {
                        setHoveredBranchUid(branch.uid);
                        setFocusedPanel("branch");
                        setFocusedIndex(index);
                      }}
                      onSelect={() => handleBranchSelect(branch.uid)}
                    />
                  ))}
                  {filteredBranches.length === 0 && (
                    <EmptyItem>{t("gnb.branchSelector.noResults")}</EmptyItem>
                  )}
                </ListPadding>
              </ScrollArea>
            </BranchListWrapper>
            <VersionPanel
              branchUid={activeBranchUid}
              selectedVersionId={selectedVersion?.id}
              onSelect={handleVersionSelect}
              focusedPanel={focusedPanel}
              focusedIndex={focusedIndex}
              versionsRef={versionsRef}
              isStaff={isAdmin}
              onMouseEnterVersion={(index) => {
                setFocusedPanel("version");
                setFocusedIndex(index);
              }}
            />
          </CascaderBody>
        </StyledContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

// BR-001: 드롭다운의 각 매장 아이템 — 자기 버전 수를 fetch 해 버전 0 이면 진입 잠금 + 툴팁 안내.
function BranchListItem({
  branch,
  isAdmin,
  isSelected,
  isHovered,
  isFocused,
  onMouseEnter,
  onSelect,
}: {
  branch: Branch;
  isAdmin: boolean;
  isSelected: boolean;
  isHovered: boolean;
  isFocused: boolean;
  onMouseEnter: () => void;
  onSelect: () => void;
}) {
  const { t } = useTranslation("shared");
  // 드롭다운 각 항목이 매장별 버전 API 를 따로 호출하면 열 때마다 N번 요청이 터짐(성능 이슈).
  // 목록 응답에 이미 실려오는 branch.hasVersion 으로 판별 → 추가 호출 0건.
  const noVersion = !branch.hasVersion;

  const item = (
    <BranchItem
      data-focused={isFocused}
      data-branch-selected={isSelected}
      $selected={isSelected}
      $hovered={isHovered}
      $focused={isFocused}
      $disabled={noVersion}
      onMouseEnter={onMouseEnter}
      onClick={() => {
        if (noVersion) return;
        onSelect();
      }}
    >
      <BranchItemLabel>
        {branch.name}
        {isAdmin && <ClientName>{branch.client.name}</ClientName>}
      </BranchItemLabel>
      {noVersion ? (
        <NoVersionTag>{t("gnb.branchSelector.noVersions")}</NoVersionTag>
      ) : (
        <ChevronIcon icon="ic_chevron_right" width={18} />
      )}
    </BranchItem>
  );

  if (noVersion) {
    return (
      <Tooltip trigger={item} side="right" sideOffset={8}>
        <TooltipText>{t("gnb.branchSelector.noVersionTooltip")}</TooltipText>
      </Tooltip>
    );
  }
  return item;
}

function VersionPanel({
  branchUid,
  selectedVersionId,
  onSelect,
  focusedPanel,
  focusedIndex,
  versionsRef,
  isStaff,
  onMouseEnterVersion,
}: {
  branchUid: string | undefined;
  selectedVersionId: number | undefined;
  onSelect: (version: BranchVersion) => void;
  focusedPanel: FocusPanel;
  focusedIndex: number;
  versionsRef: React.MutableRefObject<BranchVersion[]>;
  isStaff: boolean;
  onMouseEnterVersion: (index: number) => void;
}) {
  const { t } = useTranslation("shared");
  const { data: rawVersions } = useBranchVersions(branchUid);
  // 4-1: 고객사는 사용 중(isActive=true) 버전만 노출. 1-2 의 VersionManagementPage 정책과 일관
  const versions = isStaff ? rawVersions : rawVersions?.filter((v) => v.isActive);

  // 부모 컴포넌트에서 키보드 Enter 처리 시 버전 목록에 접근할 수 있도록 ref에 동기화
  versionsRef.current = versions ?? [];

  return (
    <VersionListWrapper>
      <ScrollArea>
        <ListPadding>
          {!branchUid || !versions || versions.length === 0 ? (
            <EmptyItem>{branchUid ? t("gnb.branchSelector.noVersions") : ""}</EmptyItem>
          ) : (
            versions.map((version, index) => (
              <VersionItem
                key={version.id}
                data-focused={focusedPanel === "version" && focusedIndex === index}
                $selected={version.id === selectedVersionId}
                $focused={focusedPanel === "version" && focusedIndex === index}
                onClick={() => onSelect(version)}
                onMouseEnter={() => onMouseEnterVersion(index)}
              >
                <StatusDot $active={version.isActive} />
                <VersionContent>
                  <VersionNameRow>
                    <VersionLabel>{version.name}</VersionLabel>
                    {index === 0 && <LatestBadge>{t("gnb.branchSelector.latestBadge")}</LatestBadge>}
                  </VersionNameRow>
                  <VersionDate>
                    {version.validFrom || version.validTo ? (
                      <>
                        {version.validFrom ? formatValidDate(version.validFrom) : ""} -{" "}
                        {version.validTo ? formatValidDate(version.validTo) : ""}
                      </>
                    ) : (
                      t("gnb.branchSelector.noValidPeriod")
                    )}
                  </VersionDate>
                </VersionContent>
              </VersionItem>
            ))
          )}
        </ListPadding>
      </ScrollArea>
    </VersionListWrapper>
  );
}

const TriggerButton = styled.button<{ $hasValue: boolean; $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 360px;
  min-width: 360px;
  max-width: 360px;
  height: 32px;
  padding: 5px 8px 3px 12px;
  border: 1px solid ${token("colors.transparent.GR600.32")};
  border-radius: 8px;
  background-color: ${token("colors.gray.white")};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.5;
  box-sizing: border-box;

  &:hover {
    border-color: ${token("colors.brand.primary")};
  }

  &[data-state="open"] {
    border-color: ${token("colors.brand.primary")};
  }

  &:disabled,
  &[aria-disabled="true"] {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const TriggerBranchName = styled.span`
  font-weight: 500;
  color: ${token("colors.text.primary")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0;
  max-width: 160px;
`;

const TriggerDivider = styled.span`
  flex-shrink: 0;
  width: 1px;
  height: 14px;
  background-color: rgba(143, 149, 178, 0.32);
`;

const TriggerVersionName = styled.span`
  font-weight: 400;
  color: ${token("colors.gray.GR800")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
  min-width: 0;
`;

const TriggerLatestBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 1px 4px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
  color: #1890ff;
  background-color: rgba(24, 144, 255, 0.16);
`;

const TriggerPlaceholder = styled.span`
  flex: 1;
  font-size: 13px;
  color: ${token("colors.text.disabled")};
`;

const TriggerChevron = styled(Icon)`
  flex-shrink: 0;
  color: ${token("colors.gray.GR800")};
  margin-left: auto;
`;

const StyledContent = styled(DropdownMenu.Content)`
  width: 478px;
  background-color: ${token("colors.gray.white")};
  border: 1px solid ${token("colors.gray.GR300")};
  border-radius: 16px;
  box-shadow: ${token("shadows.dropdown")};
  z-index: 1000;
  overflow: hidden;
`;

const ManageBranchesRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  height: 28px;
  padding: 0 12px;
  border-bottom: 1px solid rgba(143, 149, 178, 0.24);
  cursor: pointer;
  color: #696f8c;

  &:hover {
    background-color: ${token("colors.transparent.GR600.16")};
  }
`;

const ManageBranchesLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
`;

const SearchBarWrapper = styled.div`
  padding: 8px 8px 0;
`;

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px 4px 12px;
  border: 1px solid rgba(143, 149, 178, 0.32);
  border-radius: 8px;
`;

const SearchIcon = styled(Icon)`
  flex-shrink: 0;
  color: ${token("colors.text.secondary")};
`;

const SearchInputField = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
  height: 24px;

  &::placeholder {
    color: #8f95b2;
  }
`;

const CascaderBody = styled.div`
  display: flex;
  flex-direction: row;
`;

const BranchListWrapper = styled.div`
  width: 50%;
  max-height: 300px;
  border-right: 1px solid ${token("colors.gray.GR300")};
`;

const BranchItem = styled.div<{ $selected: boolean; $hovered: boolean; $focused: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 6px 7px 12px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  font-size: 12px;
  line-height: 1.5;
  color: ${({ $disabled }) => ($disabled ? token("colors.text.disabled") : token("colors.text.primary"))};
  background-color: ${({ $selected, $hovered, $focused }) =>
    $selected ? "rgba(114, 142, 255, 0.08)" : $hovered || $focused ? token("colors.transparent.GR600.16") : "transparent"};
  outline: none;

  &:hover {
    background-color: ${({ $disabled }) =>
      $disabled ? "transparent" : token("colors.transparent.GR600.16")};
  }
`;

const NoVersionTag = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  color: ${token("colors.text.disabled")};
`;

const TooltipText = styled.span`
  /* DS TooltipContent 의 슬림한 기본값(12px·line-height 1) 유지하고 줄바꿈만 방지 */
  white-space: nowrap;
`;

const BranchItemLabel = styled.span`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClientName = styled.span`
  font-size: 10px;
  line-height: 1.4;
  color: #8f95b2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChevronIcon = styled(Icon)`
  flex-shrink: 0;
  color: ${token("colors.text.secondary")};
`;

const VersionListWrapper = styled.div`
  width: 50%;
  max-height: 300px;
`;

const ListPadding = styled.div`
  padding: 8px 0;
`;

const VersionItem = styled.div<{ $selected: boolean; $focused: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 7px 6px 7px 12px;
  cursor: pointer;
  font-size: 12px;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  background-color: ${({ $selected, $focused }) =>
    $selected ? "rgba(114, 142, 255, 0.08)" : $focused ? token("colors.transparent.GR600.16") : "transparent"};
  outline: none;

  &:hover {
    background-color: ${token("colors.transparent.GR600.16")};
  }
`;

const StatusDot = styled.div<{ $active: boolean }>`
  flex-shrink: 0;
  /* 이름 줄(line-height 18px) 안에서 12px dot을 세로 중앙 정렬 → 날짜 유무와 무관하게 위치 고정 */
  margin-top: 3px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ $active }) => ($active ? "#00a925" : "rgba(143, 149, 178, 0.24)")};
`;

const VersionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const VersionNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const VersionLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LatestBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 1px 4px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
  color: #1890ff;
  background-color: rgba(24, 144, 255, 0.16);
`;

const VersionDate = styled.span`
  font-size: 12px;
  line-height: 1.17;
  color: #696f8c;
`;

const EmptyItem = styled.div`
  padding: 7px 12px;
  font-size: 12px;
  line-height: 1.5;
  color: ${token("colors.text.disabled")};
`;
